<template>
  <div class="card content-wrapper p-2">
    <div class="temporary-items-table table-component">
      <artisio-table ref="temporaryItemsTable" :pagination="tableData.pagination"
                     :sort="tableData.ordering" :items="tableData.rows" :busy="tableData.loading"
                     :fields="tableFields" :search="tableData.search" :enable-simple-search="false" selectable
                     show-toggle-columns :extra-data="extraData" @paramsChanged="getTableData"
                     @update-toggle-columns="updateToggleColumns" @on-column-save="updateToggleColumns"
                     filter-toggle-string="temporary-items-table">
        <template v-slot:header-buttons="{selectedItems}">
          <b-button v-permission="'change_temporaryinventoryitem'" v-if="!excludeActions.includes('approve')"
                    :disabled="selectedItems.size === 0" variant="primary"
                    @click="onApproveClick(selectedItems)">{{ $t('Approve') }}
          </b-button>
          <b-button v-permission="'change_temporaryinventoryitem'" v-if="!excludeActions.includes('archive')"
                    :disabled="selectedItems.size === 0" variant="danger"
                    class="ml-2" @click="onArchiveClick(selectedItems)">{{ $t('Archive') }}
          </b-button>
          <b-button v-permission="'change_temporaryinventoryitem'" v-if="!excludeActions.includes('activate')"
                    :disabled="selectedItems.size === 0" variant="primary"
                    @click="onActivateClick(selectedItems)">{{ $t('Activate') }}
          </b-button>
        </template>
        <template v-slot:search-form-placeholder>
          <search-collapse table-id="temporary-items-table">
            <temporary-items-search/>
          </search-collapse>
        </template>
        <template slot="cell(customer)" slot-scope="{item: temporaryItem}">
          <router-link v-if="$hasPermission('view_customer')"
                       :to="{name: 'customers.dashboard', params: { id: temporaryItem.customer_uuid }}">
            {{ temporaryItem.customer.display_name }}
          </router-link>
          <span v-else>
            {{ temporaryItem.customer.display_name }}
          </span>
          <action-buttons :edit-permission="'change_temporaryinventoryitem'"
                          :view-permission="'view_temporaryinventoryitem'"
                          :delete-permission="'delete_temporaryinventoryitem'"
                          :view="false" :remove="false"
                          @editClick="onEditClick(temporaryItem)"/>
        </template>
        <template slot="cell(image)" slot-scope="{item: temporaryItem}" class="text-center">
          <list-image-uploader :inventory="temporaryItem" :temporary-inventory-mode="true"
                               @imagesUploaded="onImagesUploaded"/>
        </template>
        <template slot="cell(created_at)" slot-scope="{item: temporaryItem}">
          {{ temporaryItem.created_at | toDate }}
        </template>
        <template slot="cell(archived_at)" slot-scope="{item: temporaryItem}">
          {{ temporaryItem.archived_at | toDate }}
        </template>
        <template slot="cell(low)" slot-scope="{item}">
          <template v-if="item.low || item.high">
            {{ item.low | asCurrency }} - {{ item.high | asCurrency }}
          </template>
        </template>
        <template slot="cell(receive_date)" slot-scope="{item}">
          {{ item.receive_date | toDate }}
        </template>
        <template slot="cell(reserve)" slot-scope="{item}">
          {{ item.reserve | asCurrency }}
        </template>
        <template slot="cell(progress)" slot-scope="{item}">
          {{ $t($options.filters.toUpperCaseFirst(item.progress)) }}
        </template>
        <template slot="cell(potential)" slot-scope="{item}">
          {{ $t($options.filters.toUpperCaseFirst(item.potential)) }}
        </template>
        <template slot="cell(insurance)" slot-scope="{item}">
          {{ item.insurance | asPercentage }}
        </template>
        <template slot="cell(title)" slot-scope="{item}">
          {{ item.short_description | striptags | truncate(50) }}
        </template>
        <template slot="cell(department)" slot-scope="{item}">
          {{ item.department.name | masterLanguage }}
        </template>
        <template slot="cell(category)" slot-scope="{item}">
          {{ item.category.constructed_full_path | masterLanguage }}
        </template>
        <template slot="cell(item_location)" slot-scope="{item}">
          {{ item.item_location.name }}
        </template>
        <template slot="cell(tax_status)" slot-scope="{item}">
          {{ getTaxStatusValue(item.tax_status) }}
        </template>
        <template slot="cell(created_data)" slot-scope="{item}">
          {{ item.created_at | toDate }} {{ item.created_by ? 'by ' + item.created_by.display_name : ''}}
        </template>
        <template slot="cell(updated_data)" slot-scope="{item}">
          {{ item.updated_at | toDate }} {{ item.updated_by ? 'by ' + item.updated_by.display_name : ''}}
        </template>
        <template slot="cell(specialists)" slot-scope="{item: item}">
          <template v-if="item.specialists">
            {{ item.specialists.map(s => s.display_name).join(', ') }}
          </template>
        </template>
        <template :slot="`cell(${dynamicField})`" slot-scope="{item, field}"
                  v-for="dynamicField in toggleColumns.dynamicFields">
          <span
            v-html="$options.filters.asDynamicField(getDynamicFieldValue(item, dynamicField), field.type, field.choices)">
          </span>
        </template>
        <template slot="cell(collection_info)" slot-scope="{item}">
          <div v-if="item.collection_info_full"
               v-html="$options.filters.splitAddressForMultiline(item.collection_info_full)">
          </div>
        </template>
        <template slot="cell(reserve_type)" slot-scope="{item}">
          <span v-if="item.reserve_type"> {{ item.reserve_type | removeUnderscores | toUpperCaseEveryWord }} </span>
        </template>
      </artisio-table>
      <temporary-items-approve-modal/>
      <temporary-item-modal/>
      <image-viewer ref="imageViewer"/>
    </div>
  </div>
</template>

<script>
  import {createNamespacedHelpers} from "vuex";
  import ArtisioTable from "../../../core/components/ams-table/ArtisioTable";
  import ActionButtons from "../../../core/components/ams-table/ActionButtons";
  import TemporaryItemsSearch from "./TemporaryItemsSearch";
  import SearchCollapse from "../../../core/components/search-collapse/SearchCollapse";
  import TemporaryItemsApproveModal from "@/modules/items/temporary/TemporaryItemsApproveModal";
  import {SOURCE_TEMPORARY_ITEMS_TABLE, TEMPORARY_ITEMS_TABLE_NAME} from "@/modules/constants";
  import TemporaryItemModal from "@/modules/items/temporary/TemporaryItemModal";
  import _ from "lodash";
  import ImageViewer from "@/core/components/image-viewer/ImageViewer";
  import ListImageUploader from "@/modules/inventory/ListImageUploader";

  const {mapActions, mapState} = createNamespacedHelpers('temporaryItems');
  const {mapState: globalMapState} = createNamespacedHelpers('global');

  export default {
    name: "TemporaryItemsTable",
    components: {
      ListImageUploader,
      ImageViewer,
      TemporaryItemModal,
      TemporaryItemsApproveModal, SearchCollapse, TemporaryItemsSearch, ActionButtons, ArtisioTable
    },
    props: {
      excludeActions: {
        type: Array,
        default: () => []
      },
      excludeSearchFields: {
        type: Array,
        default: () => []
      },
      excludeColumns: {
        type: Array,
        default: () => []
      },
    },
    computed: {
      ...mapState({
        tableData: state => state.tableData[state.tableSource],
        toggleColumns: state => state.tableData[state.tableSource].toggleColumns,
        tableSource: state => state.tableSource,
      }),
      ...globalMapState({
        enableInventoryQuantity: state => state.enableInventoryQuantity,
        isSalesTaxEnabled: state => state.isSalesTaxEnabled,
      }),
      tableFields() {
        return _.cloneDeep(this.toggleColumns.fields)
          .filter(field => !this.allExcludeColumns.includes(field.key) && field.isVisible)
      },
      extraData() {
        return {
          ...this.toggleColumns,
          excludeColumns: this.allExcludeColumns,
          tableName: `${TEMPORARY_ITEMS_TABLE_NAME}.${this.tableSource}`,
        }
      },
      allExcludeColumns() {
        let excludeColumns = _.cloneDeep(this.excludeColumns)
        if (!this.enableInventoryQuantity) {
          excludeColumns.push('quantity')
        }
        if (this.isSalesTaxEnabled) {
          excludeColumns.push('tax_status');
        }
        return excludeColumns
      },
    },
    methods: {
      ...mapActions([
        'showArchiveMessageModal',
        'getTableData',
        'updateSearchModel',
        'moveArchived',
        'showTemporaryItemsApproveModal',
        'showModal',
        'updateTemporaryItemLocally',
        'getModalObject',
        'updateToggleColumns'
      ]),
      onArchiveClick(items) {
        const uuids = [];
        items.forEach(item => uuids.push(item.uuid));
        this.showArchiveMessageModal({
          source: SOURCE_TEMPORARY_ITEMS_TABLE,
          uuids
        })
      },
      onActivateClick(items) {
        const uuids = [];
        items.forEach(item => uuids.push(item.uuid));
        this.$confirm(
          this.$i18n.t('Are you sure you want to activate {count} temporary item(s)?', {count: items.size}),
          this.$i18n.t('Temporary item(s) were successfully activated'),
          this.moveArchived,
          {uuids, archive: false}
        );
      },
      onApproveClick(items) {
        const uuids = [];
        let customerUuid = null;
        let customer = null;

        for (let item of items) {
          uuids.push(item.uuid);

          if (customerUuid === null) {
            customerUuid = item.customer_uuid;
            customer = item.customer;
          }

          if (customerUuid !== item.customer_uuid) {
            this.$errorDialog(this.$i18n.t('Please only select the inventory items that have been submitted by the same customer'));
            return;
          }
        }

        this.showTemporaryItemsApproveModal({
          object: {uuids, customer},
          source: SOURCE_TEMPORARY_ITEMS_TABLE
        })
      },
      async onEditClick(item) {
        this.showModal();
        this.getModalObject({uuid: item.uuid, source: SOURCE_TEMPORARY_ITEMS_TABLE});
      },
      showImageGallery(item) {
        this.$refs.imageViewer.show({
          items: item.images,
          title: item.short_description
        });
      },
      onImagesUploaded({images, uuid}) {
        const temporaryInventory = this.tableData.rows.find(item => item.uuid === uuid)
        this.updateTemporaryItemLocally({...temporaryInventory, images})
      },
      getTaxStatusValue(taxStatus) {
        if (!taxStatus) return ''
        return taxStatus.name
      },
      getDynamicFieldValue(item, field) {
        return this.$options.filters.masterLanguage(item.dynamic_fields)[field]
      },
    },
    mounted() {
      this.getTableData();
    }
  }
</script>

<style lang="scss" scoped>
  @import "./scss/variables";

  .artisio-table {
    /deep/ {

      .title-col {
        min-width: 200px;
      }

      .message-col {
        max-width: 500px;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .customer-col {
        min-width: 130px;
      }

      .estimate-col {
        min-width: 160px;
        width: 180px;
      }

      .quantity-col {
        min-width: 90px;
        width: 110px;
      }

      .reserve-col {
        min-width: 80px;
        width: 100px;
      }

      .receive-date-col {
        min-width: 100px;
        width: 150px;
      }

      .description-col {
        max-width: 500px;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .department-col {
        min-width: 100px;
      }

      .tax-status-col {
        min-width: 100px;
      }

      .category-col {
        min-width: 100px;
      }

      .item-location-col {
        min-width: 100px;
      }

      .created-data-col {
        min-width: 100px;
      }

      .updated-data-col {
        min-width: 100px;
      }

      .dynamic-fields-col {
        min-width: 100px;
        max-width: 200px;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .start-price-col {
        min-width: 100px;
      }

      .collection-info-col {
        min-width: 100px;
      }
    }
  }

</style>

